import { Popover, PopoverPanel } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import React, { type FC, useCallback, useMemo, useState } from "react";

import { DarkThemeSingleSelect, SpinnerButton } from "../../../../components";
import { useAppSelector } from "../../../../redux/hooks";
import { selectOrg } from "../../../../redux/slices/orgSlice";
import { selectPlayers } from "../../../../redux/slices/playerSlice";
import {
  type AiUploadDataRequest,
  type AiUploadDataResponse,
  useUploadAiDataMutation,
} from "../../../../services";
import { getRebootLiveMovementTypeOptions } from "../../constants";
import { usePlayerSessions, useSessionDatePicker } from "../../hooks";
import { IncludeInverseKinematicsCheckbox } from "../shared/IncludeInverseKinematicsCheckbox";
import { PopoverOpenSync } from "../shared/PopoverOpenSync";
import {
  getReportBuilderSelectStyles,
  REPORT_BUILDER_SELECT_COMPONENTS,
} from "../shared/rebootLiveSelectStyles";
import { SessionDatePicker } from "../shared/SessionDatePicker";
import {
  formatDateSelectionDisplay,
  getSessionIdsFromDates,
  MAX_SELECTABLE_SESSION_DATES,
} from "../shared/sessionDatePickerUtils";
import { SessionDatePopoverTrigger } from "../shared/SessionDatePopoverTrigger";

export interface PlayerDataSet {
  playerId: string;
  movementTypeId: string;
  sessionDates: Date[];
  sessionIds: string[];
  isLoaded: boolean;
  hasStartedLoading: boolean;
  /** True for the duration of the /ai/upload_data request */
  isUploading: boolean;
  containerId: string;
  fileIds: string[];
  filesList: {
    id: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  }[];
  dataSummary: string;
}

interface DefinePlayerDataSetProps {
  playerData: PlayerDataSet;
  onPlayerDataChange: (data: PlayerDataSet) => void;
  initContainer: (fileIds: string[], initPrompt?: string) => void;
}

type LoadingState = "idle" | "loading" | "success" | "error";

export const DefinePlayerDataSet: FC<DefinePlayerDataSetProps> = ({
  playerData,
  onPlayerDataChange,
  initContainer,
}) => {
  const players = useAppSelector(selectPlayers);
  const org = useAppSelector(selectOrg);

  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [playerSearchInput, setPlayerSearchInput] = useState("");
  const [includeInverseKinematics, setIncludeInverseKinematics] =
    useState(false);

  const {
    sessions: playerSessions,
    isFetchingAll: isFetchingAllSessions,
    error: sessionError,
  } = usePlayerSessions({ playerId: playerData.playerId });

  const updatePlayerDataFromDates = useCallback(
    (dates: Date[]) => {
      const sessionIds = getSessionIdsFromDates(dates, playerSessions);
      onPlayerDataChange({
        ...playerData,
        sessionDates: dates,
        sessionIds,
        isLoaded: false,
        containerId: "",
      });
      setLoadingState("idle");
      setErrorMessage(null);
    },
    [playerData, playerSessions, onPlayerDataChange],
  );

  const {
    tempSelectedDates,
    setTempSelectedDates,
    setIsPopoverOpen,
    handleCancel: handleDatePickerCancel,
    handleApply: handleDatePickerApply,
    handleClear: handleClearDates,
  } = useSessionDatePicker({
    selectedDates: playerData.sessionDates,
    onApply: updatePlayerDataFromDates,
    resetWhen: playerData.playerId,
  });

  // API mutations
  const [uploadAiData] = useUploadAiDataMutation();

  // Player options
  const playerOptions = players.playerList.map((player) => ({
    value: player.orgPlayerId,
    label: `${player.firstName} ${player.lastName}`,
  }));

  const movementTypeOptions = getRebootLiveMovementTypeOptions(
    org.details.movementTypes,
  );

  const selectStyles = useMemo(() => getReportBuilderSelectStyles(), []);

  const isDataSelectionComplete =
    playerData.playerId !== "" &&
    playerData.movementTypeId !== "" &&
    playerData.sessionDates.length > 0;

  const handlePlayerChange = (playerId: string) => {
    onPlayerDataChange({
      ...playerData,
      playerId: playerId || "",
      sessionDates: [],
      sessionIds: [],
      isLoaded: false,
      hasStartedLoading: false,
      isUploading: false,
      containerId: "",
      fileIds: [],
      filesList: [],
      dataSummary: "",
    });
    setLoadingState("idle");
    setErrorMessage(null);
  };

  const handleMovementTypeChange = (movementTypeId: string) => {
    onPlayerDataChange({
      ...playerData,
      movementTypeId: movementTypeId || "",
      isLoaded: false,
      containerId: "",
      fileIds: [],
      filesList: [],
      dataSummary: "",
    });
    setLoadingState("idle");
    setErrorMessage(null);
  };

  const handleLoadData = async () => {
    if (!isDataSelectionComplete || loadingState === "loading") return;

    setLoadingState("loading");
    setErrorMessage(null);
    // Enable sections as soon as loading starts, and flag the upload request
    // as in-flight so the Ready to Generate panel can show a spinner for it
    onPlayerDataChange({
      ...playerData,
      hasStartedLoading: true,
      isUploading: true,
    });

    try {
      const requestBody: AiUploadDataRequest = {
        org_player_ids: [playerData.playerId],
        movement_type_id: Number(playerData.movementTypeId),
        session_ids: playerData.sessionIds,
        ...(includeInverseKinematics
          ? { data_types: ["inverse-kinematics"] }
          : {}),
      };

      const response = await uploadAiData(requestBody).unwrap();

      const failedUploads = response?.failedUploads || [];
      const failedSessions = response?.failedSessions || [];
      const succeededSessions = response?.succeededSessions || [];
      const typedResponse = response as AiUploadDataResponse | undefined;
      const containerId = typedResponse?.containerId || "";
      const fileIds = typedResponse?.fileIds || [];
      const filesList = typedResponse?.filesList || [];
      const dataSummary = typedResponse?.dataSummary || "";

      if (failedUploads.length > 0 || failedSessions.length > 0) {
        setLoadingState("error");
        setErrorMessage(
          `${failedSessions.length} session(s) failed to load. No data found for the selected date and movement type.`,
        );
        onPlayerDataChange({ ...playerData, isUploading: false });
      } else if (succeededSessions.length > 0) {
        setLoadingState("success");
        onPlayerDataChange({
          ...playerData,
          isLoaded: true,
          isUploading: false,
          containerId,
          fileIds,
          filesList,
          dataSummary,
        });
        initContainer(fileIds);
      } else {
        setLoadingState("error");
        setErrorMessage("No data found for the selected criteria.");
        onPlayerDataChange({ ...playerData, isUploading: false });
      }
    } catch {
      setLoadingState("error");
      setErrorMessage("Failed to load data. Please try again.");
      onPlayerDataChange({ ...playerData, isUploading: false });
    }
  };

  const handleChangeData = () => {
    setLoadingState("idle");
    setErrorMessage(null);
    onPlayerDataChange({
      ...playerData,
      isLoaded: false,
      containerId: "",
      fileIds: [],
      filesList: [],
      dataSummary: "",
    });
  };

  const getPlayerName = (playerId: string) => {
    const player = players.playerList.find((p) => p.orgPlayerId === playerId);
    return player ? `${player.firstName} ${player.lastName}` : "";
  };

  const getMovementTypeName = (movementTypeId: string) => {
    const movement = org.details.movementTypes.find(
      (m) => m.id.toString() === movementTypeId,
    );
    return movement ? movement.name : "";
  };

  return (
    <div
      className="bg-[#1d1d1f] border border-white/10 rounded-lg p-6 flex flex-col gap-4"
      data-testid="define-player-data-set"
    >
      <div className="flex gap-2 items-center">
        <h2
          className="text-white font-medium"
          data-testid="define-player-data-set-title"
        >
          Define Player Data Set
        </h2>
        <span className="text-orange-500 text-xs font-medium">(Required)</span>
      </div>
      <p className="text-[#b6b2b2] text-sm mb-3">
        Select a player, movement type, and session date, then load the data to
        begin.
      </p>

      <div className="flex gap-4 items-end">
        {/* Player Dropdown */}
        <div className="flex-1">
          <label className="text-[#b6b2b2] text-sm block mb-2">Player</label>
          <DarkThemeSingleSelect
            value={playerData.playerId}
            onChange={handlePlayerChange}
            options={playerOptions}
            placeholder="Select player"
            withSearch={true}
            inputValue={playerSearchInput}
            onInputChange={setPlayerSearchInput}
            styles={selectStyles}
            className="w-full"
            classNamePrefix="analysis-builder-player"
            isDisabled={
              loadingState === "success" || loadingState === "loading"
            }
            customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
          />
        </div>

        {/* Movement Type Dropdown */}
        <div className="flex-1">
          <label className="text-[#b6b2b2] text-sm block mb-2">
            Movement Type
          </label>
          <DarkThemeSingleSelect
            value={playerData.movementTypeId}
            onChange={handleMovementTypeChange}
            options={movementTypeOptions}
            placeholder="Select movement"
            styles={selectStyles}
            className="w-full"
            classNamePrefix="analysis-builder-movement"
            isDisabled={
              loadingState === "success" || loadingState === "loading"
            }
            customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
          />
        </div>

        {/* Session Date Dropdown */}
        <div className="flex-1 relative">
          <label className="text-[#b6b2b2] text-sm block mb-2">
            Session Date
          </label>
          <Popover>
            {({ open, close }) => (
              <>
                <PopoverOpenSync open={open} onOpenChange={setIsPopoverOpen} />
                <SessionDatePopoverTrigger
                  variant="reportBuilder"
                  data-testid="define-player-data-set-session-date-button"
                  disabled={
                    loadingState === "success" ||
                    loadingState === "loading" ||
                    !playerData.playerId ||
                    playerSessions.length === 0
                  }
                  hasSelection={tempSelectedDates.length > 0}
                  displayText={
                    isFetchingAllSessions
                      ? "Loading sessions..."
                      : formatDateSelectionDisplay(
                          tempSelectedDates,
                          "Select date",
                        )
                  }
                  onClear={handleClearDates}
                />
                <PopoverPanel className="absolute z-50 top-full left-0 w-[600px] bg-[#1d1d1f] rounded-md shadow-lg border-0 p-0">
                  <SessionDatePicker
                    selectedDates={tempSelectedDates}
                    onDateChange={(dates) => {
                      setTempSelectedDates(dates);
                    }}
                    sessions={playerSessions}
                    maxSelectableDates={MAX_SELECTABLE_SESSION_DATES}
                    onCancel={() => {
                      handleDatePickerCancel();
                      close();
                    }}
                    onApply={() => {
                      handleDatePickerApply();
                      close();
                    }}
                  />
                </PopoverPanel>
              </>
            )}
          </Popover>
        </div>

        {/* Load Data Button */}
      </div>

      {/* Bottom row: data inclusion left, load data right */}
      {loadingState !== "success" && (
        <div className="flex items-center justify-between">
          <IncludeInverseKinematicsCheckbox
            checked={includeInverseKinematics}
            onChange={setIncludeInverseKinematics}
            disabled={loadingState === "loading"}
          />
          {loadingState === "loading" ? (
            <SpinnerButton
              btnText="Loading"
              className="!w-auto bg-orange-500 h-[38px] px-4 rounded-md"
            />
          ) : (
            <button
              type="button"
              onClick={handleLoadData}
              disabled={!isDataSelectionComplete}
              data-testid="define-player-data-set-load-button"
              className={`px-4 h-[38px] rounded-md font-semibold text-sm transition-all ${
                isDataSelectionComplete
                  ? "bg-orange-500 text-white hover:bg-orange-600 cursor-pointer"
                  : "bg-orange-500 text-white opacity-50 cursor-not-allowed"
              }`}
            >
              Load Data
            </button>
          )}
        </div>
      )}

      {/* Session load error (404 or failed to load) */}
      {sessionError && (
        <div className="bg-[#1d1d1f] border border-white/10 rounded-lg px-4 py-3 flex items-center gap-3">
          <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-sm text-white flex-1">{sessionError}</span>
        </div>
      )}

      {/* Error State */}
      {loadingState === "error" && errorMessage && (
        <div
          className="bg-[#1d1d1f] border border-white/10 rounded-lg px-4 py-3 flex items-center gap-3"
          data-testid="define-player-data-set-error"
        >
          <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-sm text-white flex-1">{errorMessage}</span>
          <button
            type="button"
            onClick={handleLoadData}
            className="text-orange-500 text-sm hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Success State */}
      {loadingState === "success" && (
        <div
          className="bg-[#1d2d2d] border border-white/10 rounded-lg px-5 py-4 flex items-center justify-between"
          data-testid="define-player-data-set-loaded-success"
        >
          <div className="flex gap-4 items-center">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircleIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-green-500 font-semibold">
                Data loaded successfully
              </span>
              <span className="text-gray-400 text-sm">
                <span data-testid="define-player-data-set-loaded-player-name">
                  {getPlayerName(playerData.playerId)}
                </span>
                {" • "}
                <span data-testid="define-player-data-set-loaded-movement">
                  {getMovementTypeName(playerData.movementTypeId)}
                </span>
                {" • "}
                <span data-testid="define-player-data-set-loaded-date">
                  {playerData.sessionDates.length > 0
                    ? playerData.sessionDates.length === 1
                      ? format(playerData.sessionDates[0], "M/d/yyyy")
                      : `${playerData.sessionDates.length} dates selected`
                    : ""}
                </span>
                {playerData.sessionIds.length > 1 &&
                  ` • ${playerData.sessionIds.length} sessions`}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleChangeData}
            className="text-gray-400 text-sm hover:text-white transition-colors"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
};
